import { useEffect, useState } from "react";
import { FileText, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage } from "@/lib/api";

/**
 * Tab "Riwayat Kontrak" — menampilkan seluruh periode kontrak (lama + aktif) secara kronologis.
 * Hanya tampil untuk role admin, kepala_divisi, direksi.
 */
export function ContractHistoryTab({ userId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/pegawai/${userId}/contract-history`);
      setRows(data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  return (
    <div data-testid="contract-history-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-teal-700" />
          <div className="font-semibold text-slate-900">Riwayat Kontrak</div>
          <span className="text-[11px] text-slate-500">{rows.length} periode</span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          className="gap-1.5"
          data-testid="contract-history-refresh"
        >
          <RefreshCw size={13} /> {loading ? "…" : "Muat Ulang"}
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 w-20">Periode</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 whitespace-nowrap">Awal Kontrak</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 whitespace-nowrap">Akhir Kontrak</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Keterangan</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Dicatat Oleh</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Tgl Dicatat</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">
                  Belum ada periode kontrak tercatat.
                </TableCell></TableRow>
              ) : rows.map((r, idx) => (
                <TableRow key={r.id} data-testid={`contract-row-${r.id}`} className={r.is_current ? "bg-teal-50/40" : "hover:bg-slate-50/60"}>
                  <TableCell>
                    <span className="font-bold text-slate-900 text-sm">Ke-{idx + 1}</span>
                  </TableCell>
                  <TableCell className="font-mono-clock text-xs text-slate-700 whitespace-nowrap">
                    {r.awal_kontrak || <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="font-mono-clock text-xs text-slate-700 whitespace-nowrap">
                    {r.akhir_kontrak || <span className="text-slate-300">—</span>}
                    {r.is_current ? (
                      <Badge className="ml-2 bg-teal-100 text-teal-900 border-teal-200 hover:bg-teal-100 gap-1">
                        <CheckCircle2 size={10} /> Aktif
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 italic max-w-[220px]">
                    {r.keterangan || <span className="text-slate-300 not-italic">—</span>}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.actor_name ? (
                      <>
                        <div className="font-semibold text-slate-800">{r.actor_name}</div>
                        <div className="text-[11px] text-slate-500 font-mono-clock">{r.actor_npp || "—"}</div>
                      </>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                    {fmtDate(r.recorded_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
